import React, { useEffect, useState, useRef, useLayoutEffect } from 'react';
import { motion } from 'framer-motion';
import { labService } from '../services/labService';
import SafeIcon from '@/common/SafeIcon';

const Topology = () => {
  const [repos, setRepos] = useState([]);
  const [selectedNode, setSelectedNode] = useState(null);
  const [filterActive, setFilterActive] = useState(false);

  const [edgeStats, setEdgeStats] = useState(null);
  const containerRef = useRef(null);
  const [coords, setCoords] = useState({});

  const updateCoords = () => {
    if (!containerRef.current) return;
    const containerRect = containerRef.current.getBoundingClientRect();
    const newCoords = {};
    const nodes = containerRef.current.querySelectorAll('[data-node-id]');
    nodes.forEach(node => {
      const rect = node.getBoundingClientRect();
      const id = node.getAttribute('data-node-id');
      newCoords[id] = {
        x: rect.left - containerRect.left + rect.width / 2,
        y: rect.top - containerRect.top + rect.height / 2
      };
    });
    setCoords(newCoords);
  };

  useLayoutEffect(() => {
    updateCoords();
    window.addEventListener('resize', updateCoords);
    return () => window.removeEventListener('resize', updateCoords);
  }, [repos, filterActive]);

  useEffect(() => {
    const fetchTopologyData = async () => {
      const edgeData = await labService.fetchEdgeTelemetry();
      const integrationHealth = await labService.fetchIntegrationHealth();
      setEdgeStats(edgeData);

      // Construct nodes based on live telemetry
      const nodes = [
        {
          id: 'cloudflare-worker',
          name: 'Cloudflare Worker',
          health: edgeData ? 100 : 0,
          activeSwarm: true,
          dependencies: ['supabase-db', 'github-bridge', 'llm-proxy'],
          latency: edgeData ? `${edgeData.average_latency || 0}ms` : 'N/A'
        },
        {
          id: 'passport-sso',
          name: 'Passport SSO Proxy',
          health: 100, // Assuming generally healthy
          activeSwarm: false,
          dependencies: ['cloudflare-worker'],
          latency: 'N/A'
        },
        {
          id: 'supabase-db',
          name: 'Supabase Database',
          health: 100,
          activeSwarm: false,
          dependencies: [],
          latency: 'N/A'
        },
        {
          id: 'github-bridge',
          name: 'GitHub API Bridge',
          health: integrationHealth?.github ? (integrationHealth.github.status === 'Nominal' ? 100 : (integrationHealth.github.status === 'Degraded' ? 50 : 0)) : 0,
          activeSwarm: false,
          dependencies: [],
          latency: integrationHealth?.github ? `${integrationHealth.github.latencyMs}ms` : 'N/A'
        },
        {
          id: 'llm-proxy',
          name: 'Core LLM Proxy',
          health: integrationHealth?.llm ? (integrationHealth.llm.status === 'Nominal' ? 100 : (integrationHealth.llm.status === 'Degraded' ? 50 : 0)) : 0,
          activeSwarm: false,
          dependencies: [],
          latency: integrationHealth?.llm ? `${integrationHealth.llm.latencyMs}ms` : 'N/A'
        }
      ];
      setRepos(nodes);
    };

    fetchTopologyData();
    // Refresh every 10s
    const interval = setInterval(fetchTopologyData, 10000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="max-w-7xl mx-auto space-y-6 h-[calc(100vh-140px)] flex flex-col">
      <div className="mb-4 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-white tracking-tight">Ecosystem Topology</h1>
          <p className="text-sm text-gray-400 mt-1">Real-time dependency mapping and swarm distribution</p>
        </div>
        <div className="flex bg-slate-900/90 border border-slate-800 rounded-lg p-1">
          <button
            onClick={() => setFilterActive(false)}
            className={`px-4 py-1.5 text-xs font-bold rounded-md transition-all ${!filterActive ? 'bg-blue-600 text-white' : 'text-gray-500 hover:text-gray-300'}`}
          >
            All Nodes
          </button>
          <button
            onClick={() => setFilterActive(true)}
            className={`px-4 py-1.5 text-xs font-bold rounded-md transition-all ${filterActive ? 'bg-blue-600 text-white' : 'text-gray-500 hover:text-gray-300'}`}
          >
            Active Swarms Only
          </button>
        </div>
      </div>

      <div className="flex-1 bg-slate-900/90 border border-slate-800 rounded-2xl relative overflow-hidden flex">
        {/* Grid Background */}
        <div className="absolute inset-0 opacity-10 pointer-events-none" 
             style={{ backgroundImage: 'radial-gradient(#3b82f6 1px, transparent 1px)', backgroundSize: '30px 30px' }}></div>
        
        {/* Topology View (Simplified SVG Map) */}
        <div ref={containerRef} className="flex-1 relative flex items-center justify-center">
          <svg className="absolute inset-0 w-full h-full pointer-events-none">
            {(filterActive ? repos.filter(r => r.activeSwarm) : repos).map(repo =>
              repo.dependencies.map(depId => {
                const dep = repos.find(r => r.id === depId);
                if (!dep || (filterActive && !dep.activeSwarm)) return null;
                const isActive = repo.activeSwarm && dep.activeSwarm;
                return (
                  <Connection key={`${repo.id}-${depId}`} from={repo.id} to={depId} isActive={isActive} coords={coords} />
                );
              })
            )}
          </svg>

          <div className="relative z-10 grid grid-cols-2 gap-32">
            {(filterActive ? repos.filter(r => r.activeSwarm) : repos).map((repo, idx) => (
              <Node 
                key={repo.id} 
                repo={repo} 
                isSelected={selectedNode?.id === repo.id}
                onClick={() => setSelectedNode(repo)}
              />
            ))}
          </div>
        </div>

        {/* Info Panel */}
        <div className="w-80 border-l border-slate-800 bg-[#0d1323]/50 backdrop-blur-md p-6 overflow-y-auto z-20">
          {selectedNode ? (
            <div className="space-y-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gray-800 flex items-center justify-center">
                  <SafeIcon name="Database" className="text-blue-400" />
                </div>
                <div>
                  <h3 className="text-white font-bold">{selectedNode.name}</h3>
                  <p className="text-[10px] text-gray-500 font-mono">ID: {selectedNode.id}</p>
                </div>
              </div>

              <div className="space-y-4">
                <Stat label="HEALTH" value={`${selectedNode.health}%`} color="green" />
                <Stat label="SWARM_STATUS" value={selectedNode.activeSwarm ? 'ACTIVE' : 'STANDBY'} color={selectedNode.activeSwarm ? 'blue' : 'gray'} />
                
                {selectedNode.latency && (
                  <Stat label="LATENCY" value={selectedNode.latency} color="blue" />
                )}
                {selectedNode.id === 'cloudflare-worker' && edgeStats && (
                  <Stat label="ACTIVE EDGE LOCKS" value={edgeStats.dlq_pending_count || 0} color="yellow" />
                )}
                <div className="pt-4 border-t border-slate-800">
                  <p className="text-[10px] text-gray-400 font-mono uppercase mb-2">Dependencies</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedNode.dependencies.length > 0 ? selectedNode.dependencies.map(d => (
                      <span key={d} className="px-2 py-1 bg-gray-800 text-gray-300 text-[10px] rounded border border-gray-700">
                        {repos.find(r => r.id === d)?.name}
                      </span>
                    )) : <span className="text-[10px] text-gray-600 italic">None</span>}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="h-full flex flex-col items-center justify-center text-center space-y-4">
              <SafeIcon name="Target" className="text-4xl text-gray-800" />
              <p className="text-xs text-gray-500 font-mono">SELECT A NODE TO VIEW TOPOLOGY METRICS</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const Node = ({ repo, isSelected, onClick }) => (
  <motion.button
    data-node-id={repo.id}
    whileHover={{ scale: 1.05 }}
    onClick={onClick}
    className={`w-48 p-4 rounded-xl border transition-all flex flex-col items-center gap-3 relative z-20 ${
      isSelected ? 'bg-blue-600/10 border-blue-500 shadow-[0_0_20px_rgba(59,130,246,0.2)]' : 'bg-[#111827] border-slate-800'
    }`}
  >
    <div className={`w-12 h-12 rounded-full flex items-center justify-center border-2 ${
      repo.activeSwarm ? 'border-blue-500 animate-pulse bg-blue-500/10' : 'border-gray-700 bg-gray-800'
    }`}>
      <SafeIcon name={repo.activeSwarm ? 'Cpu' : 'Database'} className={repo.activeSwarm ? 'text-blue-400' : 'text-gray-500'} />
    </div>
    <span className="text-xs font-bold text-white">{repo.name}</span>
    <div className="w-full h-1 bg-gray-800 rounded-full overflow-hidden">
      <div className={`h-full ${repo.health > 90 ? 'bg-green-500' : 'bg-yellow-500'}`} style={{ width: `${repo.health}%` }}></div>
    </div>
  </motion.button>
);

const Connection = ({ from, to, isActive, coords }) => {
  if (!coords || !coords[from] || !coords[to]) return null;
  const start = coords[from];
  const end = coords[to];

  return (
    <line
      x1={start.x} y1={start.y} x2={end.x} y2={end.y}
      stroke={isActive ? "#3b82f6" : "#1e293b"}
      strokeWidth={isActive ? "2" : "1"}
      strokeDasharray={isActive ? "none" : "4"}
      opacity={isActive ? "0.8" : "1"}
    />
  );
};

const Stat = ({ label, value, color }) => (
  <div className="bg-[#111827] p-3 rounded-lg border border-slate-800">
    <p className="text-[9px] text-gray-500 font-mono mb-1 uppercase tracking-widest">{label}</p>
    <p className={`text-sm font-bold text-${color}-400`}>{value}</p>
  </div>
);

export default Topology;